import React, { useEffect, useState } from 'react';
import {
  collection,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import {
  addNewsComment,
  deleteNewsComment,
  getTimeAgo,
} from '../firebase/newsHelpers';

export default function NewsComments({
  articleId,
  articleCollection = 'newsArticles',
  isOpen,
  currentUser,
  onCommentAdded,
}) {
  const [comments, setComments] = useState([]);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;

    const q = query(
      collection(db, articleCollection, articleId, 'comments'),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setComments(snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      })));
    });

    return () => unsubscribe();
  }, [articleCollection, articleId, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!draft.trim()) return;

    try {
      setSubmitting(true);
      await addNewsComment(articleId, currentUser, draft, articleCollection);
      setDraft('');
      onCommentAdded?.();
    } catch (error) {
      alert(error?.message || 'Could not add comment.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;

    try {
      await deleteNewsComment(articleId, commentId, articleCollection);
    } catch (error) {
      alert(error?.message || 'Could not delete comment.');
    }
  };

  return (
    <div className="news-comments">
      <div className="news-comments-list">
        {comments.length === 0 ? (
          <p className="news-comments-empty">No comments yet.</p>
        ) : (
          comments.map((comment) => (
            <article key={comment.id} className="news-comment-card">
              <div className="news-comment-header">
                <div className="news-comment-identity">
                  <span className="news-comment-name">
                    {comment.rank ? `${comment.rank} ` : ''}
                    {comment.userName || 'Unknown'}
                  </span>
                  {comment.agency ? (
                    <span className="news-comment-agency">{comment.agency}</span>
                  ) : null}
                </div>
                <div className="news-comment-meta">
                  <span>{getTimeAgo(comment.createdAt)}</span>
                  {auth.currentUser?.uid === comment.userId && (
                    <button
                      type="button"
                      className="news-comment-delete"
                      onClick={() => handleDelete(comment.id)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
              <p className="news-comment-text">{comment.text}</p>
            </article>
          ))
        )}
      </div>

      <form className="news-comment-form" onSubmit={handleSubmit}>
        <textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Add a comment..."
          rows={3}
        />
        <button type="submit" disabled={submitting || !draft.trim()}>
          {submitting ? 'Posting...' : 'Comment'}
        </button>
      </form>
    </div>
  );
}
